import { useState } from "react";
import { toast } from "sonner";
import { Crown, Check, Loader2, PiggyBank, Repeat, GitCompareArrows, MessageSquare, Mail } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

export const PRO_FEATURES = [
    { icon: PiggyBank, text: "Savings Finder — actionable cuts with £/month estimates" },
    { icon: Repeat, text: "Subscription & bill tracker with annual cost" },
    { icon: GitCompareArrows, text: "Month-on-month comparison by category" },
    { icon: Mail, text: "Monday Weekly Digest email with one thing to fix" },
    { icon: MessageSquare, text: "Unlimited Ask Sift questions (free: 5 a day)" },
];

export function startCheckout(setBusy) {
    setBusy?.(true);
    return api.post("/payments/checkout", { origin_url: window.location.origin })
        .then(({ data }) => { window.location.href = data.checkout_url; })
        .catch((e) => {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
            setBusy?.(false);
        });
}

export default function UpgradeDialog({ open, onOpenChange, feature }) {
    const [busy, setBusy] = useState(false);
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-card border-border sm:max-w-md" data-testid="upgrade-dialog">
                <DialogHeader>
                    <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-xl bg-accent/15 border border-accent/30">
                        <Crown className="h-6 w-6 text-accent" />
                    </div>
                    <DialogTitle className="font-heading text-xl text-foreground">Unlock Sift Pro</DialogTitle>
                    <DialogDescription>
                        {feature ? <><span className="font-semibold text-foreground">{feature}</span> is a Pro feature. </> : null}
                        Sift pays for itself: most people find more than £4.99 of savings in the first minute.
                    </DialogDescription>
                </DialogHeader>
                <ul className="space-y-2.5">
                    {PRO_FEATURES.map(({ icon: Icon, text }) => (
                        <li key={text} className="flex items-start gap-3 text-sm text-foreground/85">
                            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15"><Check className="h-3 w-3 text-primary" /></span>
                            <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> {text}
                        </li>
                    ))}
                </ul>
                <div className="mt-2 flex items-end justify-between rounded-xl border border-primary/30 bg-primary/10 p-4">
                    <div>
                        <p className="font-mono text-[10px] uppercase tracking-wider text-primary">Pro monthly</p>
                        <p className="font-heading text-3xl font-extrabold text-foreground" data-testid="pro-price">£4.99<span className="text-sm font-medium text-muted-foreground">/month</span></p>
                    </div>
                    <p className="text-xs text-muted-foreground">Cancel anytime</p>
                </div>
                <Button onClick={() => startCheckout(setBusy)} disabled={busy} data-testid="upgrade-checkout-btn" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                    {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Crown className="mr-2 h-4 w-4" />} Upgrade to Pro — £4.99/mo
                </Button>
                <p className="text-center text-[11px] text-muted-foreground">Secure checkout by Stripe. Billed monthly in GBP.</p>
            </DialogContent>
        </Dialog>
    );
}
